import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import {
  getSeedBookingErrorMessage,
  getSeedBookingOrderSummary,
  reviewSeedBookingOrder,
} from "./SeedBookingApi";
import {
  EmptyState,
  LoadingState,
  PageHeader,
  StatusBadge,
  formatAmount,
  formatDate,
  formatQuantity,
  getStatusMeta,
} from "./SeedBookingUi";
import "./SeedBooking.css";

const SeedBookingOrderSummary = () => {
  const { orderLineId } = useParams();
  const location = useLocation();
  const isBpUser = localStorage.getItem("userType") === "BPUser";
  const pageBase = isBpUser ? "/BPUDashboard/SeedBooking" : "/SeedBooking";
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewComments, setReviewComments] = useState("");
  const [reviewing, setReviewing] = useState(false);
  // Sales team returns to the list they opened the order from (all assigned distributors,
  // or a single distributor's list); otherwise to the list across all assigned distributors.
  const orderListPath = isBpUser
    ? location.state?.ordersPath || "/BPUDashboard/SeedBookingOrders"
    : "/SeedBookingOrders";

  useEffect(() => {
    let active = true;
    getSeedBookingOrderSummary(orderLineId)
      .then((result) => active && setOrder(result))
      .catch((error) => active && toast.error(getSeedBookingErrorMessage(error)))
      .finally(() => active && setLoading(false));

    return () => {
      active = false;
    };
  }, [orderLineId]);

  const handleReview = async (decision) => {
    setReviewing(true);
    try {
      setOrder(await reviewSeedBookingOrder({
        orderLineId: Number(orderLineId),
        decision,
        comments: reviewComments,
      }));
      toast.success(`Seed booking ${decision.toLowerCase()}.`);
    } catch (error) {
      toast.error(getSeedBookingErrorMessage(error));
    } finally {
      setReviewing(false);
    }
  };

  const statusTone = order ? getStatusMeta(order.status).tone : "neutral";
  const totalQuantity = (order?.items || []).reduce((sum, item) => sum + Number(item.quantity || 0), 0);

  return (
    <section className="content seed-booking-page">
      <div className="container-fluid">
        <PageHeader icon="fa-file-text-o" title="Booking details">
          <Link to={orderListPath} className="btn sb-btn sb-btn-outline">
            <i className="fa fa-arrow-left" aria-hidden="true"></i> All orders
          </Link>
        </PageHeader>

        {loading ? (
          <LoadingState label="Loading booking..." />
        ) : !order ? (
          <EmptyState icon="fa-exclamation-triangle" title="Booking not found">
            The seed booking could not be loaded.
          </EmptyState>
        ) : (
          <>
            <div className={`sb-card sb-hero sb-hero-${statusTone}`}>
              <div className="sb-hero-main">
                <small>Order number</small>
                <span className="sb-order-number">{order.seedBookingOrderNumber}</span>
              </div>
              <StatusBadge status={order.status} />
            </div>

            <div className="sb-facts">
              <div className="sb-card sb-fact">
                <small>Distributor</small>
                <span>{order.distributorDisplayName}</span>
              </div>
              <div className="sb-card sb-fact">
                <small>Submitted on</small>
                <span>{formatDate(order.createdOn, true)}</span>
              </div>
              <div className="sb-card sb-fact">
                <small>Products</small>
                <span>{order.items.length}</span>
              </div>
              <div className="sb-card sb-fact sb-fact-highlight">
                <small>Booking amount</small>
                <span>{formatAmount(order.subTotal)}</span>
              </div>
            </div>

            {(order.comments || order.statusChangedBy || order.statusChangeComments) && (
              <div className="sb-notes">
                {order.comments && (
                  <div className="sb-card sb-note">
                    <small><i className="fa fa-comment-o" aria-hidden="true"></i> Booking comments</small>
                    <p>{order.comments}</p>
                  </div>
                )}
                {(order.statusChangedBy || order.statusChangeComments) && (
                  <div className={`sb-card sb-note sb-note-${statusTone}`}>
                    <small>
                      <i className="fa fa-user" aria-hidden="true"></i> Reviewed by{" "}
                      {order.approvedByName || order.statusChangedBy || "sales team"}
                      {order.statusChangedOn && ` · ${formatDate(order.statusChangedOn, true)}`}
                    </small>
                    {order.statusChangeComments && <p>{order.statusChangeComments}</p>}
                  </div>
                )}
              </div>
            )}

            <div className="sb-card">
              <div className="sb-card-title">
                <h2>Booked items</h2>
                <span className="sb-pill">{order.items.length} {order.items.length === 1 ? "item" : "items"}</span>
              </div>
              <ul className="sb-rows sb-rows-readonly">
                {order.items.map((item, index) => (
                  <li key={`${item.productItemName}-${index}`} className="sb-row">
                    <div className="sb-row-main">
                      <span className="sb-row-title">{item.productItemDisplayName || item.productItemName}</span>
                      <small>{item.productLineDisplayName || item.productLineName}</small>
                    </div>
                    <div className="sb-row-figure">
                      <small>Qtl</small>
                      <span>{formatQuantity(item.quantity)}</span>
                    </div>
                    <div className="sb-row-figure sb-row-amount">
                      <small>Amount</small>
                      <span>{formatAmount(item.totalCost)}</span>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="sb-rows-total">
                <span>Booking amount · {formatQuantity(totalQuantity)} Qtl</span>
                <strong>{formatAmount(order.subTotal)}</strong>
              </div>
            </div>

            {isBpUser && order.status === "PendingForReview" && (
              <div className="sb-card sb-review">
                <div className="sb-card-title">
                  <h2>Review this booking</h2>
                </div>
                <div className="sb-form-group">
                  <label htmlFor="seedBookingReviewComments">Comments <span>(optional)</span></label>
                  <textarea
                    id="seedBookingReviewComments"
                    className="form-control"
                    maxLength="4000"
                    rows="3"
                    placeholder="Reason for approval or rejection"
                    value={reviewComments}
                    onChange={(event) => setReviewComments(event.target.value)}
                  />
                </div>
                <div className="sb-actions">
                  <button
                    type="button"
                    className="btn sb-btn sb-btn-primary"
                    onClick={() => handleReview("Approved")}
                    disabled={reviewing}
                  >
                    <i className="fa fa-check" aria-hidden="true"></i> {reviewing ? "Saving..." : "Approve"}
                  </button>
                  <button
                    type="button"
                    className="btn sb-btn sb-btn-danger"
                    onClick={() => handleReview("Rejected")}
                    disabled={reviewing}
                  >
                    <i className="fa fa-times" aria-hidden="true"></i> Reject
                  </button>
                </div>
              </div>
            )}

            <div className="sb-actions sb-actions-end">
              <Link to={orderListPath} className="btn sb-btn sb-btn-outline">View all orders</Link>
              {!isBpUser && (
                <Link
                  to={pageBase}
                  className="btn sb-btn sb-btn-primary"
                  onClick={() =>
                    localStorage.setItem("distributorId", String(order.distributorId))
                  }
                >
                  <i className="fa fa-plus" aria-hidden="true"></i> New booking
                </Link>
              )}
            </div>
          </>
        )}
      </div>
      <ToastContainer position="bottom-center" autoClose={3500} limit={2} />
    </section>
  );
};

export default SeedBookingOrderSummary;
